import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'react-bootstrap-icons';

import useGroup from '../../hooks/use_group';
import useProduct from '../../hooks/use_product';
import useTranslation from '../../hooks/use_translation';
import GroupLink from '../framework/group_link';
import Link from '../framework/link';
import EmptyTubeIcon from '../framework/svgs/icons/empty-tube-icon';
import Trans from '../translation/trans';
import ProductGroupIcon from './product-group-icon';
import ProductItemCard from './product-item-card';
import ProductItemCardSmall from './product-item-card-sm';
import ProductItemFeatured from './product-item-featured';
import ProductItemList from './product-item-list';
import ProductItemSlim from './product-item-slim';
import ProductItemTitle from './product-item-title';

const ProductList = ({
	children,
	exclude_diagnostic_types,
	exclude_groups,
	exclude_markers,
	exclude_product_settings,
	exclude_product_states,
	exclude_product_types,
	exclude_products,
	grouping = true,
	has_markers,
	include_diagnostic_types,
	include_discount,
	include_group_headers,
	include_groups,
	include_markers,
	include_product_brands,
	include_product_settings,
	include_product_states,
	include_product_types,
	include_products,
	include_tag_content,
	include_tag_types,
	limit,
	maximum_markers,
	minimum_markers,
	page = 'tester',
	search,
	sort,
	tracking,
	variant = 'list',
}) => {
	let empty = true;
	let handled = {};

	const [expandedGroups, setExpandedGroups] = useState({});
	const handleToggle = (groupId) => {
		setExpandedGroups((prevState) => ({
			...prevState,
			[groupId]: !prevState[groupId],
		}));
	};

	const variants = {
		card: {
			Component: ProductItemCard,
			groupClassName: 'd-flex slider-group w-100',
			productItemClassName:
				'product-item-card grid-slider me-1 me-lg-2 product-item-card-container',
			sectionClassName: 'd-flex',
		},
		card_grid: {
			Component: ProductItemCard,
			groupClassName: 'row',
			productItemClassName:
				'col-lg-3 col-6 px-lg-1 mb-lg-2 product-item-card-grid',
			sectionClassName: '',
		},
		card_small: {
			Component: ProductItemCardSmall,
			groupClassName: 'd-flex slider-group',
			productItemClassName:
				'grid-33-lg grid-80 me-1 me-lg-2 product-item-card-container',
			sectionClassName: '',
		},
		featured: {
			Component: ProductItemFeatured,
			groupClassName: 'product-list-group',
			productItemClassName: '',
			sectionClassName: '',
		},
		list: {
			Component: ProductItemList,
			groupClassName: 'product-list-group',
			productItemClassName: '',
			sectionClassName: '',
		},
		single_card: {
			Component: ProductItemCardSmall,
			groupClassName: 'd-flex slider-group',
			productItemClassName:
				'grid-33-lg grid-80 me-1 me-lg-2 product-item-card-container',
			sectionClassName: '',
		},
		slim: {
			Component: ProductItemSlim,
			groupClassName: 'product-list-group',
			productItemClassName: '',
			sectionClassName: 'slim-list',
		},
		title: {
			Component: ProductItemTitle,
			groupClassName: 'd-flex gap-2 flex-wrap',
			productItemClassName: '',
			sectionClassName: 'd-flex',
		},
	};

	const { t } = useTranslation('component_product_list');

	const { items: groups_data } = useGroup({
		exclude_groups,
		include_groups,
	});

	const { items: product_data } = useProduct({
		exclude_diagnostic_types,
		exclude_groups,
		exclude_markers,
		exclude_product_settings,
		exclude_product_states,
		exclude_product_types,
		exclude_products,
		has_markers,
		include_diagnostic_types,
		include_discount,
		include_groups,
		include_markers,
		include_product_brands,
		include_product_settings,
		include_product_states,
		include_product_types,
		include_products,
		include_tag_content,
		include_tag_types,
		limit,
		maximum_markers,
		minimum_markers,
		search,
		sort,
	});

	if (!variant || !variants[variant]?.Component) return <></>;
	const ProductItemComponent = variants[variant]?.Component;

	const component = (
		<section
			className={`d-inherit w-100 ${variants[variant]?.sectionClassName || ''}`}
		>
			<>
				{groups_data.map((group, index1) => {
					const product_list = product_data
						.filter((product) => {
							if (!grouping && handled[product.product_id]) return false;
							else if (!grouping && !handled[product.product_id]) return true;

							return product.groups.includes(group.group_id);
						})
						.map((product, index2) => {
							if (empty) empty = false;
							handled[product.product_id] = true;

							return (
								<div
									className={variants[variant]?.productItemClassName || ''}
									key={index2}
								>
									<ProductItemComponent
										currency={product.product_variant.currency}
										excerpt={product.product_variant.excerpt}
										group_id={grouping ? group.group_id : product.groups[0]}
										groups={product.groups}
										markers={product.markers}
										page={page}
										price={product.product_variant.price}
										price_discount={product.product_variant.price_discount}
										product_default={product.default}
										product_id={product.product_id}
										product_image={product?.fields?.files_header_url_src}
										product_settings={product.product_settings}
										product_variant_id={
											product.product_variant.product_variant_id
										}
										slug={product.product_variant.slug}
										state={product.product_variant.state}
										status={product.status}
										subtitle={product.product_variant.subtitle}
										title={product.product_variant.title}
										tracking={tracking}
										type={product.type}
									/>
								</div>
							);
						})
						.filter(Boolean);
					if (!product_list || !product_list.length) return;

					const isExpanded = expandedGroups[group.group_id];

					return (
						<div
							className={variants[variant]?.groupClassName || ''}
							key={index1}
						>
							{grouping &&
								(!include_groups ||
									(include_groups && include_groups.length > 1)) && (
									<>
										{include_group_headers ? (
											<>
												<div className="p-lg-5 p-3 rounded-3 bg-gray-100 text-center product-list-group-header mb-3">
													<ProductGroupIcon
														alt={group.group_variant.title}
														className="product-list-group-header-img"
														groups={[group.group_id]}
														type="header"
													/>
													<h2 className="product-list-group-header-title ms-1 mb-0">
														<GroupLink
															className="text-decoration-none"
															groups={[group.group_id]}
															page={page}
														>
															{group.group_variant.title}
														</GroupLink>
													</h2>
													{group.group_variant.excerpt && (
														<>
															<p
																className={`px-5 mt-3 product-list-group-header-excerpt text-muted text-break white-space-break ${
																	isExpanded ? '' : 'text-max-lines-list-fade'
																}`}
															>
																<Trans
																	defaults={group.group_variant.excerpt}
																	t={t}
																/>
															</p>
															<button
																className="btn btn-link text-dark"
																onClick={() => handleToggle(group.group_id)}
															>
																{isExpanded ? (
																	<>
																		{t('t2')} <ChevronUp />
																	</>
																) : (
																	<>
																		{t('t1')} <ChevronDown />
																	</>
																)}
															</button>
														</>
													)}
												</div>
											</>
										) : (
											<>
												<div className="d-flex align-items-center mb-4 mt-5 px-3 px-lg-0">
													<ProductGroupIcon
														alt={group.group_variant.title}
														groups={[group.group_id]}
														type="list"
													/>

													<h2 className="title-xsmall ms-1 mb-0">
														<GroupLink
															className="text-decoration-none"
															groups={[group.group_id]}
															page={page}
														>
															{group.group_variant.title}
														</GroupLink>
													</h2>
												</div>
												<hr className="mb-3" />
											</>
										)}
									</>
								)}

							{product_list}
						</div>
					);
				})}
				{empty && (variant === 'list' || variant === 'slim') && (
					<div className="text-center mt-4">
						<EmptyTubeIcon
							className="img-fluid w-100 mb-4"
							style={{ maxWidth: '150px' }}
						/>
						<p className=" fw-700 mb-1">{t('t3')}</p>
						<div>{t('t4')}</div>
						<Link className="btn btn-dark rounded-pill mt-3" to="/tester">
							{t('t5')}
						</Link>
					</div>
				)}
			</>
		</section>
	);

	return children ? <>{children(empty ? null : component)}</> : component;
};

ProductList.propTypes = {
	children: PropTypes.any,
	exclude_diagnostic_types: PropTypes.array,
	exclude_groups: PropTypes.array,
	exclude_markers: PropTypes.array,
	exclude_product_settings: PropTypes.array,
	exclude_product_states: PropTypes.array,
	exclude_product_types: PropTypes.array,
	exclude_products: PropTypes.array,
	grouping: PropTypes.bool,
	has_markers: PropTypes.bool,
	include_diagnostic_types: PropTypes.array,
	include_discount: PropTypes.bool,
	include_group_headers: PropTypes.bool,
	include_groups: PropTypes.array,
	include_markers: PropTypes.array,
	include_product_brands: PropTypes.array,
	include_product_settings: PropTypes.array,
	include_product_states: PropTypes.array,
	include_product_types: PropTypes.array,
	include_products: PropTypes.array,
	include_tag_content: PropTypes.array,
	include_tag_types: PropTypes.array,
	limit: PropTypes.number,
	maximum_markers: PropTypes.number,
	minimum_markers: PropTypes.number,
	page: PropTypes.string,
	products: PropTypes.array,
	search: PropTypes.string,
	sort: PropTypes.string,
	tracking: PropTypes.object,
	variant: PropTypes.string,
};

export default ProductList;
